<template>
  <div class="overflow-x-auto rounded-lg mt-5">
    <SearchInput
      placeholder="Search by domain"
      v-model="localSearchKeyword"
      @input="onSearchInput"
    />
    <div v-if="data?.length" class="align-middle inline-block w-full">
      <table class="divide-gray-300 w-full text-left table-auto">
        <thead class="border-b-2">
          <tr data-test="header-row">
            <th
              v-for="columnName in columnNames"
              :key="columnName"
              scope="col"
              class="px-6 py-5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              {{ columnName }}
            </th>
          </tr>
        </thead>

        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="row in data" :key="row.id">
            <td
              class="px-6 py-5 text-sm text-gray-900 text-left domain-cell break-all"
            >
              {{ row.domain }}
            </td>
            <td class="px-6 py-5 text-sm text-gray-500 text-left">
              {{ row.reason || "—" }}
            </td>
            <td
              class="px-6 py-5 text-sm text-gray-500 text-left whitespace-nowrap"
            >
              {{ formatDate(row.created_at) }}
            </td>
            <td class="px-6 py-5 text-sm text-gray-500 text-left break-all">
              {{ row.created_by_email || "—" }}
            </td>
            <td class="px-6 py-5 text-sm text-gray-500 text-left actions-cell">
              <div class="flex">
                <button
                  type="button"
                  @click="openPopup(row)"
                  class="button-icon"
                  aria-label="Edit"
                  title="Edit banned domain"
                >
                  <PencilIcon class="h-5" />
                </button>
                <button
                  type="button"
                  @click="onDeleteDomain(row)"
                  class="button-icon pl-3"
                  aria-label="Delete"
                  title="Remove from ban list"
                >
                  <TrashIcon class="h-6" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-else class="px-6 py-4 text-sm text-gray-500 text-center">
      {{ emptyMessage }}
    </div>
    <Paginator
      ref="paginator"
      :onPageChange="onPageChange"
      :data="paginationData"
      :pageSize="15"
    />
  </div>
  <teleport to="body">
    <transition name="fade">
      <div v-if="showPopup" class="popup-container">
        <Form @submit="onSubmit" v-slot="{ errors }" class="space-y-6">
          <div
            class="popup inline-block align-bottom bg-white text-left shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-xl sm:max-h-md w-full rounded-lg"
          >
            <h3 class="text-lg leading-6 font-medium text-gray-900">
              <div class="pb-3">Update banned domain</div>
            </h3>
            <div class="sm:col-span-6">
              <div class="pb-3">
                <label for="edit-domain"> Domain </label>
                <div class="mt-1">
                  <Field
                    id="edit-domain"
                    name="domain"
                    type="text"
                    v-model="domain"
                    rules="required"
                    class="form-field-no-icon w-full"
                  />
                  <p class="text-red-500 text-sm mt-1">
                    {{ capitalize(errors.domain) }}
                  </p>
                </div>
              </div>

              <div class="pb-3">
                <label for="edit-reason"> Reason </label>
                <div class="mt-1">
                  <Field
                    id="edit-reason"
                    name="reason"
                    type="text"
                    v-model="reason"
                    placeholder="Optional"
                    class="form-field-no-icon w-full"
                  />
                  <p class="text-red-500 text-sm mt-1">
                    {{ errors.reason }}
                  </p>
                </div>
              </div>
            </div>
            <div
              class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse rounded-b"
            >
              <button
                type="submit"
                class="button-submit sm:ml-3"
                :disabled="saving"
              >
                Update
              </button>
              <button
                type="button"
                class="button-cancel ml-0 sm:ml-3"
                @click="closePopup()"
              >
                Cancel
              </button>
            </div>
          </div>
        </Form>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { ref } from "vue";
import moment from "moment";
import { TrashIcon } from "@heroicons/vue/outline";
import { PencilIcon } from "@heroicons/vue/solid";
import eventBus from "@/utils/eventBus";
import useConfirmationModal from "@/api/confirmationModal";
import formMixin from "@/mixins/formMixin";
import { Field, Form } from "vee-validate";
import Paginator from "@/components/common/Paginator";
import SearchInput from "@/components/common/SearchInput";

export default {
  name: "BannedDomainsTable",
  components: {
    TrashIcon,
    PencilIcon,
    Field,
    Form,
    Paginator,
    SearchInput,
  },
  mixins: [formMixin],
  props: {
    data: {
      type: Array,
      required: true,
    },
    paginationData: {
      type: Object,
      required: true,
    },
    searchKeyword: {
      type: String,
      default: "",
    },
    onPageChange: {
      type: Function,
      required: true,
    },
    onSearch: {
      type: Function,
      required: true,
    },
  },
  beforeMount() {
    eventBus.trigger("update-banned-domains");
  },
  setup() {
    const columnNames = ref([
      "Domain",
      "Reason",
      "Added",
      "Added by",
      "Actions",
    ]);

    return {
      columnNames,
      id: ref(null),
      domain: ref(""),
      reason: ref(""),
      showPopup: ref(false),
      saving: ref(false),
      localSearchKeyword: ref(""),
    };
  },
  computed: {
    emptyMessage() {
      return this.searchKeyword
        ? "No domains match your search."
        : "No banned domains.";
    },
  },
  methods: {
    // SearchInput already debounces its `input` event (300 ms), so this runs
    // once per pause in typing, not once per keystroke.
    async onSearchInput() {
      this.$refs.paginator.page = 1;
      await this.onSearch(this.localSearchKeyword);
    },
    formatDate(value) {
      return value ? moment(value).format("YYYY-MM-DD HH:mm") : "—";
    },
    capitalize(string) {
      if (string) {
        return string.charAt(0).toUpperCase() + string.slice(1);
      }
      return string;
    },
    async onSubmit(values, actions) {
      this.saving = true;
      const response = await this.handleSubmissionErrors(
        () =>
          this.$alfredService.bannedDomainEdit(this.id, {
            domain: values.domain,
            reason: values.reason || "",
          }),
        { formActions: actions }
      );
      this.saving = false;
      if (response) {
        eventBus.trigger("notify", {
          notification: {
            group: "top-right",
            text: "Domain updated.",
            type: "success",
          },
        });
        this.closePopup();
        eventBus.trigger("update-banned-domains");
      }
    },
    async onDeleteDomain(row) {
      const modal = useConfirmationModal();
      await modal.openModal({
        title: "Remove banned domain?",
        message: `Accounts on ${row.domain} will be able to sign up and sign in again.`,
        primaryButton: "Remove",
        onSubmit: async () => {
          try {
            await this.handleSubmissionErrors(
              () => this.$alfredService.bannedDomainDelete(row.id),
              { throwExceptions: true }
            );
          } catch (e) {
            return;
          }
          eventBus.trigger("notify", {
            notification: {
              group: "top-right",
              type: "success",
              text: "Domain removed from the ban list.",
            },
          });
          const currentPage = this.$refs.paginator.page;
          if (this.data.length === 1 && currentPage > 1) {
            this.$refs.paginator.page = currentPage - 1;
            await this.onPageChange(currentPage - 1);
          } else {
            eventBus.trigger("update-banned-domains");
          }
        },
      });
    },
    openPopup(row) {
      this.id = row.id;
      this.domain = row.domain;
      this.reason = row.reason || "";
      this.showPopup = true;
    },
    closePopup() {
      this.showPopup = false;
      this.id = null;
      this.domain = "";
      this.reason = "";
    },
  },
};
</script>

<style scoped>
.domain-cell {
  min-width: 200px;
}
.actions-cell {
  width: 100px;
}
</style>

<style>
/* The popup is teleported to <body>, so these rules cannot be scoped. */
.popup-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
}
.popup {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
}
</style>
